'use client';

import * as React from 'react';
import clsx from 'clsx';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';

type Direction = 'up' | 'down' | 'left' | 'right' | 'none';

export interface ScrollRevealProps
  extends React.HTMLAttributes<HTMLDivElement> {
  as?: keyof React.JSX.IntrinsicElements;
  delay?: number;
  duration?: number;
  direction?: Direction;
  amount?: number;
  once?: boolean;
  margin?: string;
  disabled?: boolean;
  threshold?: number;
  children: React.ReactNode;
  onReveal?: (isVisible: boolean) => void;
}

export function ScrollReveal({
  children,
  className,
  style,
  delay = 0,
  duration = 0.8, // Increased default duration for smoother animations
  direction = 'up',
  amount = 0.2,
  once = true,
  margin,
  disabled = false,
  threshold = 0.1,
  onReveal,
  ...rest
}: ScrollRevealProps) {
  // Use intersection observer to detect when element is visible
  // Default margin triggers animation slightly before element is fully visible for smoother experience
  const defaultMargin = margin || '0px 0px -5% 0px'; // Reduced margin for earlier, smoother triggering

  const { ref, inView } = useInView({
    threshold: threshold,
    triggerOnce: once,
    rootMargin: defaultMargin,
    skip: disabled,
  });

  // Call onReveal callback when visibility changes
  React.useEffect(() => {
    if (onReveal) {
      onReveal(inView);
    }
  }, [inView, onReveal]);

  // Calculate initial position based on direction
  const getInitialPosition = () => {
    switch (direction) {
      case 'up':
        return { y: amount * 100, opacity: 0 };
      case 'down':
        return { y: -(amount * 100), opacity: 0 };
      case 'left':
        return { x: amount * 100, opacity: 0 };
      case 'right':
        return { x: -(amount * 100), opacity: 0 };
      case 'none':
      default:
        return { opacity: 0 };
    }
  };

  const getAnimatePosition = () => {
    switch (direction) {
      case 'up':
      case 'down':
        return { y: 0, opacity: 1 };
      case 'left':
      case 'right':
        return { x: 0, opacity: 1 };
      case 'none':
      default:
        return { opacity: 1 };
    }
  };

  // If disabled, render without animation
  if (disabled) {
    return (
      <div className={clsx(className)} {...rest}>
        {children}
      </div>
    );
  }

  // Use motion.div as wrapper for all elements to ensure compatibility
  // Add style to prevent overflow issues and enable hardware acceleration
  const motionStyle: React.CSSProperties = {
    // Merge with passed style prop
    ...style,
    // Prevent transforms from causing overflow by containing them
    // Only apply containment for non-none directions to avoid breaking layouts
    ...(direction !== 'none' && {
      willChange: 'transform, opacity',
      // Use isolation to create a new stacking context without affecting layout
      isolation: 'isolate',
      // Contain layout to prevent transforms from causing overflow
      contain: 'layout style paint',
      // Enable hardware acceleration for smoother animations
      transform: 'translateZ(0)',
      backfaceVisibility: 'hidden',
      perspective: '1000px',
    }),
  };

  // Create safe props by excluding conflicting event handlers
  const safeProps = Object.fromEntries(
    Object.entries(rest).filter(
      ([key]) =>
        ![
          'onDrag',
          'onDragStart',
          'onDragEnd',
          'onAnimationStart',
          'onAnimationEnd',
        ].includes(key)
    )
  ) as Omit<
    React.HTMLAttributes<HTMLDivElement>,
    | 'onDrag'
    | 'onDragStart'
    | 'onDragEnd'
    | 'onAnimationStart'
    | 'onAnimationEnd'
  >;

  return (
    <motion.div
      ref={ref}
      className={clsx(className)}
      style={motionStyle}
      initial={getInitialPosition()}
      animate={inView ? getAnimatePosition() : getInitialPosition()}
      transition={{
        duration: duration,
        delay: delay,
        ease: [0.16, 1, 0.3, 1], // Smooth ease-out-expo curve for very smooth animations
        type: 'tween', // Use tween for smoother animations
      }}
      {...safeProps}
    >
      {children}
    </motion.div>
  );
}
